import React, { useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { UserTaskMimshakData, UserTaskStatus, UserTaskType } from "../../interfaces/DTO/userTask/UserTask";
import { colorUserTaskStatus, colorUserTaskType } from "../../utils/colorEnumFormatter";
import { formatUserTaskStatus, formatUserTaskType } from "../../utils/enumFormatter";

interface Props {
  tasks: UserTaskMimshakData[];
}

const metricRowSx = {
  color: "#6d1727",
  fontWeight: 500,
};

const countBy = <K extends string>(tasks: UserTaskMimshakData[], getKey: (task: UserTaskMimshakData) => K) =>
  Object.entries(
    tasks.reduce((acc, task) => {
      const key = getKey(task);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  )
    .map(([key, count]) => ({ key: key as K, count }))
    .sort((a, b) => b.count - a.count);

/** סיכום הפעולות (userTaskMimshakData) שנמצאו — לפי סוג פעולה וסטטוס יצרן */
const UserTaskMimshakSummary: React.FC<Props> = ({ tasks }) => {
  const byType = useMemo(() => countBy<UserTaskType>(tasks, (t) => t.user_task_type), [tasks]);
  const byStatus = useMemo(() => countBy<UserTaskStatus>(tasks, (t) => t.user_task_status), [tasks]);

  if (tasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
        לא נמצאו פעולות
      </Typography>
    );
  }

  return (
    <Box sx={{ textAlign: "center", px: 2 }}>
      {/* פס יחסי לפי סוג פעולה */}
      <Box sx={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", mb: 1.5 }}>
        {byType.map(({ key, count }) => (
          <Tooltip key={key} title={`${formatUserTaskType(key)}: ${count}`}>
            <Box sx={{ flex: count, bgcolor: colorUserTaskType(key), borderLeft: "2px solid #fff" }} />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {byType.map(({ key, count }) => (
          <Box key={key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorUserTaskType(key), flexShrink: 0 }} />
              <Typography variant="body2" sx={{ ...metricRowSx, textAlign: "right" }}>
                {formatUserTaskType(key)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ...metricRowSx, fontWeight: 700 }}>
              {count}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* פילוח לפי סטטוס יצרן */}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5, mt: 1.5 }}>
        {byStatus.map(({ key, count }) => (
          <Chip
            key={key}
            label={`${formatUserTaskStatus(key)} ${count}`}
            size="small"
            sx={{ bgcolor: colorUserTaskStatus(key), color: "#fff", fontWeight: 600, borderRadius: "6px" }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default UserTaskMimshakSummary;
